import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'

interface SectionHeadingProps {
    eyebrow?: ReactNode
    title: ReactNode
    subtitle?: ReactNode
    action?: { href: string, label: string }
    align?: 'start' | 'center'
    tone?: 'dark' | 'light'
    className?: string
    id?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({ eyebrow, title, subtitle, action, align = 'start', tone = 'dark', className, id }): ReactNode => {
    const { isRtl } = useI18n()
    const Arrow = isRtl ? ArrowLeft : ArrowRight

    return (
        <div className={cn('flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between', align === 'center' && 'items-center text-center sm:flex-col sm:items-center', className)}>
            <div className={cn('flex max-w-2xl flex-col gap-3', align === 'center' && 'items-center')}>
                {eyebrow && <span className={cn('text-sm font-semibold', tone === 'light' ? 'text-cyan-300' : 'text-cyan-700')}>{eyebrow}</span>}
                <h2 id={id} className={cn('text-balance text-3xl font-bold leading-tight tracking-tight sm:text-4xl', tone === 'light' ? 'text-white' : 'text-navy-950')}>{title}</h2>
                {subtitle && <div className={cn('text-pretty text-base leading-relaxed sm:text-lg', tone === 'light' ? 'text-white/70' : 'text-muted-foreground')}>{subtitle}</div>}
            </div>

            {action && (
                <Link href={action.href} className={cn('group inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold transition-colors', tone === 'light' ? 'text-cyan-300 hover:text-cyan-200' : 'text-navy-900 hover:text-cyan-700')}>
                    {action.label}
                    <Arrow className='size-4 transition-transform duration-200 group-hover:-translate-x-0.5 ltr:group-hover:translate-x-0.5' aria-hidden='true' />
                </Link>
            )}
        </div>
    )
}
